import * as React from 'react';
import { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { SyncIcon, ExclamationTriangleIcon, ArrowRightIcon } from '@patternfly/react-icons';
import toast from 'react-hot-toast';
import { AdminOverview } from '@app/Overview/types';
import { getOverview } from '@app/Overview/overview.api';
import { relativeTime } from '@app/Screens/format';
import { STATUS_META } from '@app/Screens/StatusBadge';
import { colors, radii, shadows } from '@app/components/designTokens';

const REFRESH_MS = 30000;

export const Overview: React.FunctionComponent = () => {
  const navigate = useNavigate();
  const [data, setData] = useState<AdminOverview | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [updatedAt, setUpdatedAt] = useState<string | null>(null);

  const load = useCallback(async (spinner: boolean) => {
    if (spinner) setLoading(true);
    try {
      const d = await getOverview();
      setData(d);
      setError(null);
      setUpdatedAt(new Date().toISOString());
    } catch (e: any) {
      setError(e?.message || 'Failed to load metrics');
      if (!spinner) toast.error('Could not refresh metrics');
    } finally {
      if (spinner) setLoading(false);
    }
  }, []);

  useEffect(() => {
    load(true);
    const t = setInterval(() => load(false), REFRESH_MS);
    return () => clearInterval(t);
  }, [load]);

  const styles: { [k: string]: React.CSSProperties } = {
    wrap: { padding: 24 },
    headerRow: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12, marginBottom: 20 },
    title: { fontSize: 22, fontWeight: 700, color: colors.title, margin: 0 },
    subtle: { fontSize: 13, color: colors.subtle },
    refreshBtn: {
      display: 'inline-flex', alignItems: 'center', gap: 8, padding: '8px 14px',
      border: `1px solid ${colors.border}`, borderRadius: radii.button, background: '#fff',
      color: colors.body, fontSize: 13, fontWeight: 600, cursor: 'pointer'
    },
    grid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: 14, marginBottom: 22 },
    sectionTitle: { fontSize: 15, fontWeight: 700, color: colors.title, margin: '0 0 12px' },
    panels: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: 14 },
    panel: { background: '#fff', border: `1px solid ${colors.border}`, borderRadius: radii.card, boxShadow: shadows.softCard, padding: 18 }
  };

  if (loading) {
    return (
      <div style={styles.wrap}>
        <div style={{ ...styles.panel, padding: 48, textAlign: 'center', color: colors.subtle }}>
          Loading platform metrics…
        </div>
      </div>
    );
  }

  if (error && !data) {
    return (
      <div style={styles.wrap}>
        <div style={{ background: colors.dangerRing, color: colors.danger, borderRadius: 12, padding: 20 }}>{error}</div>
      </div>
    );
  }

  const d = data as AdminOverview;

  const attention = [
    { label: 'screens offline', count: d.screensOffline, to: '/screens' },
    { label: 'runs awaiting driver approval', count: d.runsPendingApproval, to: '/campaigns' },
    { label: 'runs paused', count: d.runsPaused, to: '/campaigns' }
  ].filter(a => a.count > 0);

  return (
    <div style={styles.wrap}>
      <div style={styles.headerRow}>
        <div>
          <h1 style={styles.title}>Platform Overview</h1>
          <div style={styles.subtle}>
            {updatedAt ? `Updated ${relativeTime(updatedAt)} · auto-refreshes every 30s` : ''}
          </div>
        </div>
        <button style={styles.refreshBtn} onClick={() => load(true)}>
          <SyncIcon /> Refresh
        </button>
      </div>

      {/* Attention needed */}
      <AttentionPanel items={attention} onGo={navigate} />

      {/* KPI tiles */}
      <div style={styles.grid}>
        <KpiTile label="Total users" value={d.totalUsers} onClick={() => navigate('/users')} />
        <KpiTile
          label="Screens online"
          value={`${d.screensOnline}/${d.screensTotal}`}
          sub={`${d.screensStale} stale · ${d.screensOffline} offline`}
          accent={STATUS_META.ONLINE.dot}
          onClick={() => navigate('/screens')}
        />
        <KpiTile
          label="Active campaigns"
          value={d.campaignsActive}
          sub={`${d.campaignsTotal} total · ${d.campaignsCreatedLast7d} new this week`}
          onClick={() => navigate('/campaigns')}
        />
        <KpiTile
          label="Live runs"
          value={d.runsInProgress}
          sub={`${d.runsPaused} paused · ${d.runsPendingApproval} pending approval`}
          onClick={() => navigate('/campaigns')}
        />
        <KpiTile
          label="AI ads created"
          value={d.aiAdsTotal}
          sub={`${d.adsTotal} ads total · ${d.adsCreatedLast7d} new this week`}
        />
        <KpiTile
          label="Subscriptions"
          value={d.subscriptionsTotal}
          sub={`${d.subscriptionsByStatus['ACTIVE'] || 0} active · ${d.subscriptionsByStatus['TRIALING'] || 0} trialing`}
          onClick={() => navigate('/billing')}
        />
      </div>

      {/* Distributions */}
      <h2 style={styles.sectionTitle}>Distributions</h2>
      <div style={styles.panels}>
        <div style={styles.panel}>
          <DistributionBars title="Campaigns by state" data={d.campaignsByState} color={colors.accent} />
        </div>
        <div style={styles.panel}>
          <DistributionBars title="Subscriptions by tier" data={d.subscriptionsByTier} color="#0ea5e9" />
        </div>
        <div style={styles.panel}>
          <DistributionBars title="Subscriptions by status" data={d.subscriptionsByStatus} color="#059669" />
        </div>
        <div style={styles.panel}>
          <h3 style={{ fontSize: 14, fontWeight: 700, color: colors.title, margin: '0 0 14px' }}>Ad creation</h3>
          <div style={{ display: 'flex', gap: 24 }}>
            <MiniStat label="Last 7 days" value={d.adsCreatedLast7d} />
            <MiniStat label="Last 30 days" value={d.adsCreatedLast30d} />
            <MiniStat label="AI ratio" value={d.adsTotal ? `${Math.round((d.aiAdsTotal / d.adsTotal) * 100)}%` : '—'} />
          </div>
        </div>
      </div>
    </div>
  );
};

// ---------- presentational helpers ----------

const KpiTile: React.FunctionComponent<{
  label: string;
  value: React.ReactNode;
  sub?: string;
  accent?: string;
  onClick?: () => void;
}> = ({ label, value, sub, accent, onClick }) => (
  <div
    onClick={onClick}
    style={{
      background: '#fff',
      border: `1px solid ${colors.border}`,
      borderRadius: radii.card,
      boxShadow: shadows.softCard,
      padding: '18px 20px',
      cursor: onClick ? 'pointer' : 'default',
      position: 'relative'
    }}
  >
    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
      {accent && <span style={{ width: 9, height: 9, borderRadius: '50%', background: accent }} />}
      <span style={{ fontSize: 13, fontWeight: 600, color: colors.subtle }}>{label}</span>
    </div>
    <div style={{ fontSize: 30, fontWeight: 800, color: colors.title, marginTop: 6 }}>{value}</div>
    {sub && <div style={{ fontSize: 12, color: colors.subtle, marginTop: 4 }}>{sub}</div>}
  </div>
);

const MiniStat: React.FunctionComponent<{ label: string; value: React.ReactNode }> = ({ label, value }) => (
  <div>
    <div style={{ fontSize: 26, fontWeight: 800, color: colors.title }}>{value}</div>
    <div style={{ fontSize: 12, color: colors.subtle }}>{label}</div>
  </div>
);

const DistributionBars: React.FunctionComponent<{
  title: string;
  data: Record<string, number>;
  color: string;
}> = ({ title, data, color }) => {
  const entries = Object.entries(data || {}).sort((a, b) => b[1] - a[1]);
  const max = Math.max(1, ...entries.map(e => e[1]));
  return (
    <>
      <h3 style={{ fontSize: 14, fontWeight: 700, color: colors.title, margin: '0 0 14px' }}>{title}</h3>
      {entries.length === 0 ? (
        <div style={{ fontSize: 13, color: colors.placeholder }}>No data yet.</div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {entries.map(([key, count]) => (
            <div key={key} style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <div style={{ width: 130, fontSize: 12, color: colors.muted, textAlign: 'right', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                {key}
              </div>
              <div style={{ flex: 1, background: colors.bgMuted, borderRadius: 6, height: 18, overflow: 'hidden' }}>
                <div style={{ width: `${(count / max) * 100}%`, background: color, height: '100%', borderRadius: 6, minWidth: count > 0 ? 4 : 0 }} />
              </div>
              <div style={{ width: 36, fontSize: 13, fontWeight: 700, color: colors.title, textAlign: 'right' }}>{count}</div>
            </div>
          ))}
        </div>
      )}
    </>
  );
};

const AttentionPanel: React.FunctionComponent<{
  items: { label: string; count: number; to: string }[];
  onGo: (to: string) => void;
}> = ({ items, onGo }) => {
  if (items.length === 0) {
    return (
      <div
        style={{
          background: 'rgba(5, 150, 105, 0.08)',
          border: '1px solid rgba(5, 150, 105, 0.25)',
          borderRadius: radii.card,
          padding: '14px 18px',
          color: '#047857',
          fontWeight: 600,
          fontSize: 14,
          marginBottom: 22
        }}
      >
        All clear — no screens offline, no runs awaiting approval or paused.
      </div>
    );
  }
  return (
    <div
      style={{
        background: 'rgba(245, 158, 11, 0.08)',
        border: '1px solid rgba(245, 158, 11, 0.3)',
        borderRadius: radii.card,
        padding: 18,
        marginBottom: 22
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12, color: '#b45309', fontWeight: 700 }}>
        <ExclamationTriangleIcon /> Attention needed
      </div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10 }}>
        {items.map(it => (
          <button
            key={it.label}
            onClick={() => onGo(it.to)}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 8,
              background: '#fff',
              border: `1px solid ${colors.border}`,
              borderRadius: radii.button,
              padding: '10px 14px',
              cursor: 'pointer',
              fontSize: 14,
              color: colors.body
            }}
          >
            <strong style={{ color: colors.warning, fontSize: 16 }}>{it.count}</strong>
            {it.label}
            <ArrowRightIcon style={{ color: colors.subtle }} />
          </button>
        ))}
      </div>
    </div>
  );
};
